/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Page, PageHeader, Button, LinkButton, EmptyState, Skeleton } from '../../components/console/ui';
import ConfirmModal from '../../components/common/ConfirmModal';
import { managedServiceApi } from '../../services/api';
import { formatMoney, StatusBadge } from './ManagedInstances';
import type { ManagedServiceInstance, ManagedServiceCredentials, ManagedServiceProduct } from '../../types/managedServices';

const PRODUCT_NAMES: Record<ManagedServiceProduct, string> = {
  mlflow: 'MLflow',
  kfp: 'Kubeflow Pipelines',
  dvc: 'DVC',
  metaflow: 'Metaflow',
};

const POLLING_STATUSES = ['provisioning', 'deleting'];

// The DVC S3-compatible endpoint is served on a sibling host: the instance's
// first hostname label gets a "-s3" suffix (e.g. "dvc-ab12cd34.apps..." ->
// "dvc-ab12cd34-s3.apps..."). See podstack-dvc chart's `s3Host` helper.
const deriveDvcS3Host = (endpointUrl: string): string => {
  try {
    const host = new URL(endpointUrl).host;
    const parts = host.split('.');
    if (parts.length > 1) {
      return [`${parts[0]}-s3`, ...parts.slice(1)].join('.');
    }
    return `${host}-s3`;
  } catch {
    return '';
  }
};

interface ConnectLine {
  label: string;
  value: string;
  /** When true, `value` is a full shell command/snippet - render as-is (no "label=" prefix). */
  raw?: boolean;
}

/**
 * Builds the product-specific "Connect" snippet lines shown on an active
 * instance's detail page. Kept pure (no React state) so it's easy to reason
 * about / extend per product.
 */
const buildConnectLines = (
  instance: ManagedServiceInstance,
  credentials: ManagedServiceCredentials | null
): ConnectLine[] => {
  const host = instance.endpoint_url ? new URL(instance.endpoint_url).host : '';
  const user = credentials?.username;
  const pass = credentials?.password;

  switch (instance.product) {
    case 'mlflow':
      return [
        { label: 'MLFLOW_TRACKING_URI', value: `https://${host}` },
        { label: 'MLFLOW_TRACKING_USERNAME', value: user ?? '<reveal credentials>' },
        { label: 'MLFLOW_TRACKING_PASSWORD', value: pass ?? '<reveal credentials>' },
      ];
    case 'dvc': {
      const s3Host = instance.endpoint_url ? deriveDvcS3Host(instance.endpoint_url) : '';
      return [
        { label: 'dvc remote add', value: 'dvc remote add -d podstack s3://dvc', raw: true },
        { label: 'dvc remote modify', value: `dvc remote modify podstack endpointurl https://${s3Host}`, raw: true },
        { label: 'Access key', value: user ?? '<reveal credentials>' },
        { label: 'Secret key', value: pass ?? '<reveal credentials>' },
      ];
    }
    case 'metaflow':
      return [
        { label: 'METAFLOW_SERVICE_URL', value: `https://${host}/metadata/` },
        { label: 'Basic-auth username', value: user ?? '<reveal credentials>' },
        { label: 'Basic-auth password', value: pass ?? '<reveal credentials>' },
      ];
    case 'kfp':
    default:
      return [
        { label: 'UI', value: `https://${host}` },
        { label: 'SDK', value: `kfp.Client(host='https://${host}', ...)` },
        { label: 'Basic-auth username', value: user ?? '<reveal credentials>' },
        { label: 'Basic-auth password', value: pass ?? '<reveal credentials>' },
      ];
  }
};

const showTopUpToast = (msg: string) => {
  toast(
    (t) => (
      <div className="space-y-2">
        <p className="text-sm font-medium text-white">Insufficient wallet balance</p>
        <p className="text-xs text-gray-300">{msg}</p>
        <div className="flex gap-2">
          <a
            href="/portal/wallet?action=topup"
            onClick={() => toast.dismiss(t.id)}
            className="text-xs px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white rounded"
          >
            Top up wallet
          </a>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-xs px-3 py-1 border border-gray-600 text-gray-300 rounded"
          >
            Dismiss
          </button>
        </div>
      </div>
    ),
    { duration: 15000, style: { background: '#1f2937', color: '#fff', maxWidth: 480 } }
  );
};

const ManagedInstanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Backend keeps the instance `suspended` for up to ~5 min while it scales back up
  // after a resume request (202 {"status":"resuming"}). Track that locally so the
  // page keeps polling and shows progress instead of a dead-ended "suspended" state
  // with an enabled Resume button.
  const [resumeRequested, setResumeRequested] = useState(false);

  // Credentials are fetched on demand into local component state - they are
  // NEVER put into the React Query cache (which can be inspected via devtools
  // and persists across the session) or refetched by polling.
  const [credentials, setCredentials] = useState<ManagedServiceCredentials | null>(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [credsError, setCredsError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    data: instance,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['managed-service', id],
    queryFn: () => managedServiceApi.get(id as string).then((res) => res.data as ManagedServiceInstance),
    enabled: !!id,
    refetchInterval: (query) => {
      const inst = query.state.data as ManagedServiceInstance | undefined;
      if (!inst) return false;
      if (POLLING_STATUSES.includes(inst.status)) return 5000;
      if (resumeRequested && inst.status === 'suspended') return 5000;
      return false;
    },
  });

  // Clear the resume flag once the instance actually leaves the suspended state
  // (resumed to active, or failed) so the Resume button / progress copy don't
  // stick around forever.
  useEffect(() => {
    if (resumeRequested && instance && instance.status !== 'suspended') {
      setResumeRequested(false);
    }
  }, [resumeRequested, instance]);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const revealCredentials = async () => {
    if (!id) return;
    setCredsLoading(true);
    setCredsError(null);
    try {
      const res = await managedServiceApi.getCredentials(id);
      setCredentials(res.data as ManagedServiceCredentials);
      setShowPassword(false);
    } catch (err: any) {
      setCredsError(err?.response?.data?.error || 'Failed to fetch credentials');
    } finally {
      setCredsLoading(false);
    }
  };

  const hideCredentials = () => {
    setCredentials(null);
    setCredsError(null);
    setShowPassword(false);
  };

  const resumeMutation = useMutation({
    mutationFn: () => managedServiceApi.resume(id as string),
    onSuccess: (res) => {
      if (res?.status === 202) {
        setResumeRequested(true);
      }
      toast.success('Resuming - this may take a couple of minutes');
      queryClient.invalidateQueries({ queryKey: ['managed-service', id] });
      queryClient.invalidateQueries({ queryKey: ['managed-services'] });
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to resume instance';
      if (status === 402) {
        showTopUpToast(msg);
        return;
      }
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => managedServiceApi.remove(id as string),
    onSuccess: () => {
      toast.success('Instance is being deleted');
      queryClient.invalidateQueries({ queryKey: ['managed-services'] });
      navigate('/managed-mlops/instances');
    },
    onError: (err: any) => {
      setShowDeleteConfirm(false);
      toast.error(err?.response?.data?.error || 'Failed to delete instance');
    },
  });

  if (isLoading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }

  if (isError || !instance) {
    return (
      <Page>
        <EmptyState
          title="Couldn’t load this instance"
          body="Failed to load this managed instance."
          actions={
            <>
              <Button onClick={() => refetch()}>Retry</Button>
              <LinkButton to="/managed-mlops/instances" variant="secondary">
                Back to instances
              </LinkButton>
            </>
          }
        />
      </Page>
    );
  }

  const total = instance.monthly_cost + instance.storage_monthly_cost;

  return (
    <Page>
      <PageHeader
        back={{ to: '/managed-mlops/instances', label: 'My instances' }}
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {instance.name}
            <StatusBadge status={instance.status} failureReason={instance.failure_reason} />
          </span>
        }
        subtitle={<span className="capitalize">{`${PRODUCT_NAMES[instance.product] || instance.product} · ${instance.tier} tier`}</span>}
        actions={
          <>
            {instance.status === 'active' && instance.endpoint_url && (
              <LinkButton to={instance.endpoint_url} external>
                Open web UI
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </LinkButton>
            )}
            {instance.status === 'suspended' && (
              <Button onClick={() => resumeMutation.mutate()} disabled={resumeMutation.isPending || resumeRequested}>
                <ArrowPathIcon
                  className={`w-4 h-4 ${resumeMutation.isPending || resumeRequested ? 'animate-spin' : ''}`}
                />
                {resumeMutation.isPending || resumeRequested ? 'Resuming...' : 'Resume'}
              </Button>
            )}
            {instance.status !== 'deleting' && instance.status !== 'deleted' && (
              <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                <TrashIcon className="w-4 h-4" />
                Delete
              </Button>
            )}
          </>
        }
      />

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status card */}
            <div className="ps-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Status</h3>
                <StatusBadge status={instance.status} failureReason={instance.failure_reason} />
              </div>

              {instance.status === 'provisioning' && (
                <p className="text-sm text-gray-400">
                  Your {PRODUCT_NAMES[instance.product] || instance.product} server is being provisioned. Usually ready
                  in a few minutes.
                </p>
              )}
              {instance.status === 'deleting' && (
                <p className="text-sm text-gray-400">This instance is being deleted.</p>
              )}
              {instance.status === 'suspended' && resumeRequested && (
                <p className="text-sm text-gray-400">
                  Resuming - usually takes a couple of minutes. This page will update automatically.
                </p>
              )}
              {instance.status === 'suspended' && !resumeRequested && (
                <p className="text-sm text-gray-400">
                  This instance is suspended (renewal not paid). Resume it to restore access - you'll be charged the
                  renewal amount immediately.
                </p>
              )}
              {instance.status === 'failed' && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-300">
                      {instance.failure_reason || 'Provisioning failed.'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Delete this instance and order a new one from the catalog to retry.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <p className="text-sm text-gray-400">Created</p>
                  <p className="text-sm font-medium text-white">
                    {format(new Date(instance.created_at), 'MMM d, yyyy HH:mm')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Storage</p>
                  <p className="text-sm font-medium text-white">{instance.storage_gb} GB</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Next renewal</p>
                  <p className="text-sm font-medium text-white">
                    {instance.next_renewal_at ? format(new Date(instance.next_renewal_at), 'MMM d, yyyy') : '-'}
                  </p>
                </div>
              </div>

              {instance.endpoint_url && (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-1">Endpoint</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm font-mono text-gray-300 overflow-x-auto">
                      {instance.endpoint_url}
                    </code>
                    <button
                      onClick={() => copyToClipboard(instance.endpoint_url as string, 'endpoint')}
                      className="p-2 text-gray-400 hover:text-gray-300 bg-gray-800 border border-gray-700 rounded-lg flex-shrink-0"
                      title="Copy endpoint"
                    >
                      {copiedField === 'endpoint' ? (
                        <CheckIcon className="w-5 h-5 text-green-400" />
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {instance.status === 'active' && instance.endpoint_url && (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-1">Connect</p>
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-1.5">
                    {buildConnectLines(instance, credentials).map((line) => {
                      const fieldKey = `connect-${line.label}`;
                      return (
                        <div key={fieldKey} className="flex items-center gap-2">
                          <code className="flex-1 text-xs font-mono text-gray-300 overflow-x-auto whitespace-nowrap">
                            {!line.raw && <span className="text-gray-500">{line.label}=</span>}
                            {line.value}
                          </code>
                          <button
                            onClick={() => copyToClipboard(line.value, fieldKey)}
                            className="p-1 text-gray-400 hover:text-gray-300 flex-shrink-0"
                            title={`Copy ${line.label}`}
                          >
                            {copiedField === fieldKey ? (
                              <CheckIcon className="w-4 h-4 text-green-400" />
                            ) : (
                              <ClipboardDocumentIcon className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {!credentials && (
                    <p className="text-xs text-gray-500 mt-1">Reveal credentials below to fill in the username/password lines.</p>
                  )}
                  {instance.product === 'metaflow' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Use <code className="font-mono">/metadata/</code>, not <code className="font-mono">/api</code> - the
                      ui-backend mirror under <code className="font-mono">/api</code> is read-only.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Credentials card */}
            <div className="ps-card p-5">
              <h3 className="text-lg font-semibold text-white mb-4">Credentials</h3>
              {!credentials ? (
                <div>
                  <p className="text-sm text-gray-400 mb-3">
                    Credentials are only shown when you request them - they are never cached.
                  </p>
                  <button
                    onClick={revealCredentials}
                    disabled={credsLoading}
                    className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <EyeIcon className="w-4 h-4" />
                    {credsLoading ? 'Fetching...' : 'Reveal credentials'}
                  </button>
                  {credsError && <p className="text-sm text-red-400 mt-2">{credsError}</p>}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Username</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm font-mono text-gray-200 overflow-x-auto">
                        {credentials.username}
                      </code>
                      <button
                        onClick={() => copyToClipboard(credentials.username, 'username')}
                        className="p-2 text-gray-400 hover:text-gray-300 bg-gray-800 border border-gray-700 rounded-lg flex-shrink-0"
                        title="Copy username"
                      >
                        {copiedField === 'username' ? (
                          <CheckIcon className="w-5 h-5 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Password</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm font-mono text-gray-200 overflow-x-auto">
                        {showPassword ? credentials.password : '•'.repeat(Math.max(credentials.password.length, 8))}
                      </code>
                      <button
                        onClick={() => setShowPassword((s) => !s)}
                        className="p-2 text-gray-400 hover:text-gray-300 bg-gray-800 border border-gray-700 rounded-lg flex-shrink-0"
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(credentials.password, 'password')}
                        className="p-2 text-gray-400 hover:text-gray-300 bg-gray-800 border border-gray-700 rounded-lg flex-shrink-0"
                        title="Copy password"
                      >
                        {copiedField === 'password' ? (
                          <CheckIcon className="w-5 h-5 text-green-400" />
                        ) : (
                          <ClipboardDocumentIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <button onClick={hideCredentials} className="text-xs text-gray-400 hover:text-white">
                    Hide credentials
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Side column */}
          <div className="space-y-6">
            <div className="ps-card p-5">
              <h3 className="text-lg font-semibold text-white mb-4">Monthly cost</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">{PRODUCT_NAMES[instance.product] || instance.product} ({instance.tier})</span>
                  <span className="text-gray-200">{formatMoney(instance.monthly_cost, instance.currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Storage ({instance.storage_gb} GB)</span>
                  <span className="text-gray-200">{formatMoney(instance.storage_monthly_cost, instance.currency)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-800 font-semibold">
                  <span className="text-white">Total</span>
                  <span className="text-white">{formatMoney(total, instance.currency)}/mo</span>
                </div>
              </div>
            </div>

            <div className="ps-card p-5">
              <h3 className="text-lg font-semibold text-white mb-4">Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Instance ID</span>
                  <span className="text-gray-200 font-mono text-xs truncate max-w-[10rem]" title={instance.id}>
                    {instance.id}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Project</span>
                  <span className="text-gray-200 font-mono text-xs truncate max-w-[10rem]" title={instance.project_id}>
                    {instance.project_id}
                  </span>
                </div>
                {instance.last_billed_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Last billed</span>
                    <span className="text-gray-200">{format(new Date(instance.last_billed_at), 'MMM d, yyyy')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
        isLoading={deleteMutation.isPending}
        title="Delete managed instance"
        message={`Delete "${instance.name}"? This permanently destroys the instance and all of its data. This cannot be undone.`}
        confirmText="Delete instance"
        variant="danger"
      />
    </Page>
  );
};

export default ManagedInstanceDetail;
